import { TabNote } from "@/types/tablatures";
import React from "react";

export class TabElement implements TabNote {
    alt: number = 0;
    sign: string = "";
    key: string = "";
    type: string = "";
    index: number = -1; // Added index property
}

interface TablatureElementProps {
    note: TabNote;
    index: number;
    isSelected?: boolean;
    isHighlighted?: boolean;
    onClick?: (note: TabNote, index: number) => void;
}

interface TablatureLine {
    notes: TabNote[];
    lineIndex: number;
}

export class TabConverter {

    public static toNotes = function (textNotes: string): TabNote[] {
        // First, split by line breaks to handle each line separately
        const lines = textNotes.split('\n');
        const result: TabNote[] = [];
        let noteCounter = 0; // Initialize noteCounter

        for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
            const line = lines[lineIndex];

            if (line.trim() === '') {
                // Empty line, add a line break element
                if (lineIndex > 0) {
                    const lineBreakElement = new TabElement();
                    lineBreakElement.key = '|';
                    result.push(lineBreakElement);
                }
                continue;
            }

            // Use regex to split while preserving multiple spaces
            // const parts = line.split(/( {2,})/);

            // for (const part of parts) {
            // if (part.match(/^ {2,}$/)) {
            // This is multiple spaces, add space elements for each extra space
            // const spaceCount = part.length - 1; // One less than the number of spaces
            // for (let i = 0; i < spaceCount; i++) {
            //     const spaceElement = new TabElement();
            //     spaceElement.key = '_';
            //     result.push(spaceElement);
            // }
            // } else if (part.trim() !== '') {
            // This contains notes, split by single spaces and process
            const notes = line.split(' ').filter(note => note !== '');
            for (let i = 0; i < notes.length; i++) {
                const note = notes[i];
                const noteObj = TabConverter.toNoteObject(note.trim());

                // Only assign index to actual notes (not separators, etc) if they are playable
                const isPlayable = !['|', '|:', ':|', '||', '_', 'SPACE', '/', '(', ')', '!'].includes(noteObj.key as string);
                if (isPlayable) {
                    (noteObj as TabElement).index = noteCounter++;
                }

                // Look-ahead for parenthesis alignment logic
                // If this is a '(', look forward to find if notes inside are Draw (-)
                if (noteObj.key === '(') {
                    let j = i + 1;
                    let hasDraw = false;
                    while (j < notes.length) {
                        const nextNote = notes[j];
                        if (nextNote.includes(')')) break; // approximate check
                        if (nextNote.includes('-')) {
                            hasDraw = true;
                        }
                        j++;
                    }
                    if (hasDraw) {
                        noteObj.sign = '-'; // Mark opening bracket as Draw for alignment
                    }
                }

                // If checking for ')', we need to share the same alignment. 
                // Simplified: if the *previous matching* '(' was draw, this should be too.
                // But doing it statelessly in linear pass is hard unless we track it.
                // Or just apply the same logic in reverse or assume consistnecy.
                // Better: Check the last added '(' in result? No, result isn't fully built.
                // Let's just track state in loop.

                result.push(noteObj);
            }

            // Post-process line to match ')' alignment to '('
            let lastOpenParenIndex = -1;
            for (let i = 0; i < result.length; i++) {
                if (result[i].key === '(') {
                    lastOpenParenIndex = i;
                }
                if (result[i].key === ')' && lastOpenParenIndex !== -1) {
                    result[i].sign = result[lastOpenParenIndex].sign;
                }
            }

            // }
            // }

            // Add line break after each line (except the last one)
            if (lineIndex < lines.length - 1) {
                // Check if the last element of this line is already a separator
                const lastElement = result[result.length - 1];
                if (!lastElement || (lastElement.key !== '|' && lastElement.key !== '||' && lastElement.key !== ':|')) {
                    const lineBreakElement = new TabElement();
                    lineBreakElement.key = '|';
                    result.push(lineBreakElement);
                }
            }
        }

        return result;
    }

    /**
     * Convert note (e.g: "1", "-2", "5''") to TabElement object.
     */
    public static toNoteObject = function (note: any): TabNote {
        let sign = '';
        let alt = '';
        let n = '';
        let type = '';

        // Handle special symbols first
        switch (note) {
            case '|':
                n = '|';
                break;
            case '|:':
                n = '|:';
                break;
            case ':|':
                n = ':|';
                break;
            case '||':
                n = '||';
                break;
            case '_':
                n = '_';
                break;
            case '/':
                n = '/';
                break;
            case '(':
                n = '(';
                break;
            case ')':
                n = ')';
                break;
            default:
                // Check if it contains a number (including negative numbers)
                const numberMatch = note.match(/-?\d{1,2}/);
                if (numberMatch) {
                    // Extract sign (if negative number)
                    if (note.startsWith('-')) {
                        sign = '-';
                    } else if (note.startsWith('+')) {
                        sign = '';
                    }

                    // Extract alteration marks
                    const altMatches = note.match(/'/g);
                    alt = altMatches ? altMatches.length.toString() : '';

                    // Extract type markers
                    type = note.match(/\.{2}/) ? '..' : '';

                    // Extract the number (without sign)
                    n = Math.abs(parseInt(numberMatch[0])).toString();
                } else {
                    if (note !== ' ' && note !== '') {
                        console.error(`${new Date().toISOString()} [Next.js] ERROR  features/tabconverter.toNoteObject: invalid note`, JSON.stringify(note));
                        n = '!';
                    }
                }
        }

        const tab = new TabElement();
        tab.alt = parseInt(alt, 10) || 0;
        tab.sign = sign;
        tab.key = n;
        tab.type = type;
        return tab;
    };

    public static fromNotes(partition: TabNote[]): string {
        return " " + partition.map(this.fromNoteToText).join(' ') || '';
    }

    public static fromNoteToText(n: TabNote): string {
        switch (n.key) {
            case '|':
                return n.key + '\n';
            case '|:':
                return n.key;
            case ':|':
                return n.key;
            case '||':
                return n.key;
            // case 'SPACE':
            // return ''; // Don't output text for internal space key
            case '_':
                return n.key;
            case '/':
                return n.key;
            case '(':
                return n.key;
            case ')':
                return n.key;
            case '!':
                return n.key;
            default:
                let k = n.key;
                k = n.sign === '-' ? '-' + n.key : n.key;
                for (let i = 0; i < n.alt; i++) {
                    k += '\'';
                }
                return k.toString();
        }
    }

    /**
     * Convert partition string to modern HTML elements with full-width lines
     */
    public static toNewHTML(partition: string, contentWidth: number, activeNoteIndices: number[] = []): React.ReactElement[] {
        if (!contentWidth || contentWidth === 0) {
            console.error('contentWidth has invalid value: ', contentWidth);
            return [];
        }

        const notes = this.toNotes(partition);
        const layout = this.calculateLayout(notes, contentWidth);

        return this.renderTablatureLines(layout, activeNoteIndices);
    }

    /**
     * Calculate responsive layout for tablature with full-width lines
     */
    private static calculateLayout(notes: TabNote[], contentWidth: number): TablatureLine[] {
        const elWidth = 30;
        const startElementWidth = 30;
        const availableWidth = contentWidth - startElementWidth;
        const elementsPerLine = Math.floor(availableWidth / elWidth);

        // Ensure minimum elements per line
        const minElementsPerLine = Math.max(elementsPerLine, 5);
        const lines: TablatureLine[] = [];

        for (let i = 0; i < notes.length; i += minElementsPerLine) {
            const lineNotes = notes.slice(i, i + minElementsPerLine);

            // Fill remaining space with empty elements if needed for full width
            while (lineNotes.length < minElementsPerLine && i + lineNotes.length < notes.length + minElementsPerLine - 1) {
                const emptyNote = new TabElement();
                emptyNote.key = 'SPACE'; // Changed to SPACE
                lineNotes.push(emptyNote);
            }

            lines.push({
                notes: lineNotes,
                lineIndex: Math.floor(i / minElementsPerLine)
            });
        }

        return lines;
    }

    /**
     * Convert partition string to linear HTML elements (1 line text)
     */
    public static toLinearHTML(partition: string, activeNoteIndices: number[] = []): React.ReactElement[] {
        // Linear mode simply renders the notes in sequence, wrapping at container width naturally if we used flex-wrap,
        // but to ensure '1 line' feels like traditional text tablature, we often want lines.
        // However, user asked for "1 seule ligne qui affiche les tablatures traditionnellement".
        // This implies just a sequence of text nodes: "-4 5 -5" etc.

        const notes = this.toNotes(partition);
        const elements: React.ReactElement[] = [];

        notes.forEach((note, index) => {
            const isHighlighted = activeNoteIndices.includes((note as TabElement).index);
            // Create a simple text representation
            let text = this.fromNoteToText(note);
            if (note.key === '|' || note.key === '||') {
                // Add some spacing around bars
            }
            if (note.key === 'SPACE') return; // Skip visual spaces in linear mode? Or keep them as space char?
            // If text is empty (SPACE), render whitespace
            if (note.key === 'SPACE') text = '\u00A0\u00A0'; // Non-breaking spaces for alignment

            elements.push(
                <span
                    key={`linear-${index}`}
                    className={`
                        inline-block px-1 font-mono text-lg
                        ${isHighlighted ? 'highlighted text-amber-600 font-bold' : 'text-gray-700'}
                        ${(note.key === '|' || note.key === '||') ? 'text-gray-400 font-light mx-2' : ''}
                        transition-all duration-100
                    `}
                >
                    {text}
                </span>
            );

            // Add a line break for newlines in original text if we want to preserve some structure?
            // "1 seule ligne" might mean literally one infinite line or just "single staff" vs "double staff/hole visual".
            // Usually "traditional" means lines of text. 
            // We'll let the container wrap them.
        });

        return elements;
    }

    /**
     * Render tablature lines as full-width containers
     */
    private static renderTablatureLines(lines: TablatureLine[], activeNoteIndices: number[] = []): React.ReactElement[] {
        const result: React.ReactElement[] = [];

        lines.forEach((line, lineIndex) => {
            const lineElements: React.ReactElement[] = [];

            // Add start element for each line
            lineElements.push(this.createStartElement(lineIndex));

            // Add notes for this line
            line.notes.forEach((note, noteIndex) => {
                const globalIndex = lineIndex * 1000 + noteIndex; // Ensure unique keys
                const isHighlighted = activeNoteIndices.includes((note as TabElement).index);
                lineElements.push(this.createTablatureElement(note, globalIndex, { isHighlighted }));
            });

            // Wrap line in a container for full width
            result.push(
                <div
                    key={`tablature-line-${lineIndex}`}
                    className="tablature-line"
                    style={{
                        display: 'flex',
                        width: '100%',
                        marginBottom: '2px',
                        alignItems: 'center'
                    }}
                >
                    {lineElements}
                </div>
            );
        });

        return result;
    }

    /**
     * Create start element component
     */
    private static createStartElement(lineIndex: number): React.ReactElement {
        return (
            <span className="tabElement tabStart" key={`start-${lineIndex}`}>
                <div className="topElement">
                    <span className="value">A</span>
                </div>
                <div className="middleLine" />
                <div className="bottomElement">
                    <span className="value">S</span>
                </div>
            </span>
        );
    }

    /**
     * Create appropriate tablature element based on note key
     */
    private static createTablatureElement(note: TabNote, index: number, props?: Partial<TablatureElementProps>): React.ReactElement {
        const elementProps = {
            note,
            index,
            isSelected: props?.isSelected || false,
            isHighlighted: props?.isHighlighted || false,
            // onClick: props?.onClick
        };

        switch (note.key) {
            case '|':
                return this.createSeparatorElement(elementProps);
            case '|:':
                return this.createLeftRepeatElement(elementProps);
            case ':|':
                return this.createRightRepeatElement(elementProps);
            case '||':
                return this.createNoteElement(elementProps);
            case '_':
            case 'SPACE':
                return this.createSpaceElement(elementProps);
            case '/':
                return this.createBreakerElement(elementProps);
            case '(':
                return this.createLeftBracketElement(elementProps);
            case ')':
                return this.createRightBracketElement(elementProps);
            case '!':
                return this.createErrorElement(elementProps);
            default:
                // @ts-ignore
                if (parseInt(note.key) > 0 && parseInt(note.key) < 11) {
                    return this.createNoteElement(elementProps);
                } else {
                    return this.createErrorElement(elementProps);
                }
        }
    }

    /**
     * Create separator element component
     */
    private static createSeparatorElement(props: TablatureElementProps): React.ReactElement {
        return (
            <span
                className={`tabElement ${props.isSelected ? 'selected' : ''} ${props.isHighlighted ? 'highlighted' : ''}`}
                key={`separator-${props.index}`}
            // onClick={() => props.onClick?.(props.note, props.index)}
            >
                <div className="separator" />
                <div className="middleLine" />
            </span>
        );
    }

    /**
     * Create left repeat element component
     */
    private static createLeftRepeatElement(props: TablatureElementProps): React.ReactElement {
        return (
            <span
                className={`tabElement leftRepeat ${props.isSelected ? 'selected' : ''} ${props.isHighlighted ? 'highlighted' : ''}`}
                key={`lrepeat-${props.index}`}
            // onClick={() => props.onClick?.(props.note, props.index)}
            >
                <div className="repeatLine" />
                <div className="topElement">:</div>
                <div className="middleLine" />
                <div className="bottomElement">:</div>
            </span>
        );
    }

    /**
     * Create right repeat element component
     */
    private static createRightRepeatElement(props: TablatureElementProps): React.ReactElement {
        return (
            <span
                className={`tabElement rightRepeat ${props.isSelected ? 'selected' : ''} ${props.isHighlighted ? 'highlighted' : ''}`}
                key={`rrepeat-${props.index}`}
            // onClick={() => props.onClick?.(props.note, props.index)}
            >
                <div className="repeatLine" />
                <div className="topElement">:</div>
                <div className="middleLine" />
                <div className="bottomElement">:</div>
            </span>
        );
    }

    /**
     * Create space element component
     */
    private static createSpaceElement(props: TablatureElementProps): React.ReactElement {
        return (
            <span
                className={`tabElement ${props.isSelected ? 'selected' : ''} ${props.isHighlighted ? 'highlighted' : ''}`}
                key={`space-${props.index}`}
            // onClick={() => props.onClick?.(props.note, props.index)}
            >
                <div className="topElement" />
                <div className="middleLine" />
                <div className="bottomElement" />
            </span>
        );
    }

    /**
     * Create breaker element component
     */
    private static createBreakerElement(props: TablatureElementProps): React.ReactElement {
        return (
            <span
                className={`tabElement ${props.isSelected ? 'selected' : ''} ${props.isHighlighted ? 'highlighted' : ''}`}
                key={`breaker-${props.index}`}
            // onClick={() => props.onClick?.(props.note, props.index)}
            >
                <div className="breaker" />
                <div className="middleLine" />
            </span>
        );
    }

    /**
     * Create left bracket element component
     */
    private static createLeftBracketElement(props: TablatureElementProps): React.ReactElement {
        return (
            <span
                className={`tabElement bracket ${props.isSelected ? 'selected' : ''} ${props.isHighlighted ? 'highlighted' : ''}`}
                key={`lbracket-${props.index}`}
            // onClick={() => props.onClick?.(props.note, props.index)}
            >
                <div className="topElement">{props.note.sign === '-' ? '(' : ''}</div>
                <div className="middleLine" />
                <div className="bottomElement">{props.note.sign !== '-' ? '(' : ''}</div>
            </span>
        );
    }

    /**
     * Create right bracket element component
     */
    private static createRightBracketElement(props: TablatureElementProps): React.ReactElement {
        return (
            <span
                className={`tabElement bracket ${props.isSelected ? 'selected' : ''} ${props.isHighlighted ? 'highlighted' : ''}`}
                key={`rbracket-${props.index}`}
            // onClick={() => props.onClick?.(props.note, props.index)}
            >
                <div className="topElement">{props.note.sign === '-' ? ')' : ''}</div>
                <div className="middleLine" />
                <div className="bottomElement">{props.note.sign !== '-' ? ')' : ''}</div>
            </span>
        );
    }

    /**
     * Create error element component
     */
    private static createErrorElement(props: TablatureElementProps): React.ReactElement {
        return (
            <span
                className={`tabElement error ${props.isSelected ? 'selected' : ''} ${props.isHighlighted ? 'highlighted' : ''}`}
                key={`error-${props.index}`}
            // onClick={() => props.onClick?.(props.note, props.index)}
            >
                <div className="topElement">
                    <span className="value">!</span>
                </div>
                <div className="middleLine" />
                <div className="bottomElement">
                    <span className="value">!</span>
                </div>
            </span>
        );
    }

    /**
     * Create note element component with extensible properties
     */
    private static createNoteElement(props: TablatureElementProps): React.ReactElement {
        let alt = '';
        for (let i = 0; i < props.note.alt; i++) {
            alt += '\'';
        }

        const noteContent = (
            <span className="value">
                {props.note.key + alt + props.note.type}
            </span>
        );

        return (
            <span
                className={`tabElement ${props.isSelected ? 'selected' : ''} ${props.isHighlighted ? 'highlighted' : ''}`}
                key={`note-${props.index}`}
            // onClick={() => props.onClick?.(props.note, props.index)}
            >
                <div className="topElement">
                    {props.note.sign === '-' ? noteContent : ''}
                </div>
                <div className="middleLine" />
                <div className="bottomElement">
                    {props.note.sign !== '-' ? noteContent : ''}
                </div>
            </span>
        );
    }
}